<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useMilestonesStore } from '../stores/milestones'

const props = defineProps({
  fixtureId: {
    type: [String, Number],
    required: true
  },
  fileName: {
    type: String,
    default: 'milestones-data'
  },
  selectedMarketType: {
    type: String,
    default: null
  }
})

const milestonesStore = useMilestonesStore()
const isDownloading = ref(false)
const showTooltip = ref(false)
const showPopup = ref(false)
const downloadType = ref('single') // 'single' or 'multiple'
const hoveredOption = ref(null) // Track which option is being hovered
const showJsonMarkets = ref(false) // Show market picker for JSON export
const defaultJsonMarketNames = ['points', 'rebounds', 'three_point_field_goal', 'assists']
const selectedJsonMarkets = ref([])
const jsonMarketsInitialized = ref(false)

// Computed property to get available market types from data
const availableMarketTypes = computed(() => {
  const data = milestonesStore.getMilestones
  if (!data || data.length === 0) return []
  return [...new Set(data.map(item => item.market_type).filter(Boolean))].sort()
})

// When available markets load, pre-select the defaults (case-insensitive match)
watch(availableMarketTypes, (markets) => {
  if (markets.length > 0 && !jsonMarketsInitialized.value) {
    selectedJsonMarkets.value = markets.filter(m =>
      defaultJsonMarketNames.includes(m.toLowerCase())
    )
    jsonMarketsInitialized.value = true
  }
}, { immediate: true })

// Computed property to check if there's data to download
const hasData = computed(() => {
  return milestonesStore.getMilestones && 
         milestonesStore.getMilestones.length > 0
})

// Function to convert milestone data to grid format CSV
const convertToCSV = (data, marketType = null) => {
  if (!data || data.length === 0) return ''
  
  // Filter data by market type if specified and exclude suspended items
  const filteredData = (marketType ? data.filter(item => item.market_type === marketType) : data)
    .filter(item => {
      // Only show items where is_suspended is false
      return item.is_suspended == false
    })
  
  // Debug: Log the first few items to see the data structure
  console.log('Original data length:', data?.length)
  console.log('Filtered data length:', filteredData.length)
  console.log('First few milestone items:', filteredData.slice(0, 3))
  console.log('Sample milestone item structure:', filteredData[0])
  
  // Helper function to get over odds value
  const getOverOdds = (item) => {
    return item.milestone_over_odds || item.over_odds || item.odds1 || item.odds || ''
  }
  
  // Helper function to get under odds value
  const getUnderOdds = (item) => {
    return item.milestone_under_odds || item.under_odds || item.odds2 || ''
  }
  
  // Helper function to check if suspended
  const isSuspended = (item) => {
    return (item.milestone_suspended === 1 || item.suspended === 1) ? 'Yes' : 'No'
  }
  
  // Group data by player
  const playerGroups = {}
  filteredData.forEach(item => {
    const playerId = item.player_id
    if (!playerGroups[playerId]) {
      playerGroups[playerId] = {
        player_name: item.player?.player_name || 'Unknown Player',
        team_name: item.player_team_name || item.home_team_name || item.away_team_name || 'Unknown Team',
        milestones: {}
      }
    }
    // Store milestone by its line value
    const milestoneKey = item.milestone_line ? item.milestone_line.toFixed(1) : '0.0'
    playerGroups[playerId].milestones[milestoneKey] = {
      over_odds: getOverOdds(item),
      under_odds: getUnderOdds(item),
      suspended: isSuspended(item)
    }
  })
  
  // Get all unique milestone lines and sort them
  const allMilestoneLines = new Set()
  filteredData.forEach(item => {
    if (item.milestone_line) {
      allMilestoneLines.add(item.milestone_line.toFixed(1))
    }
  })
  const sortedMilestoneLines = Array.from(allMilestoneLines).sort((a, b) => parseFloat(a) - parseFloat(b))
  
  // Create headers
  const headers = ['Player Name', 'Team', ...sortedMilestoneLines.map(line => `TO REACH: ${line}`)]
  
  // Create rows - sorted by team, then by player name
  const rows = Object.values(playerGroups)
    .sort((a, b) => {
      // First sort by team name
      const teamComparison = (a.team_name || '').localeCompare(b.team_name || '')
      if (teamComparison !== 0) return teamComparison
      // Then sort by player name
      return (a.player_name || '').localeCompare(b.player_name || '')
    })
    .map(player => {
      const row = [player.player_name, player.team_name]
      
      // Add milestone values for each line
      sortedMilestoneLines.forEach(line => {
        const milestone = player.milestones[line]
        if (milestone) {
          // Always show over odds value, regardless of suspension status
          const value = milestone.over_odds || '0.00'
          row.push(value)
        } else {
          row.push('0.00')
        }
      })
      
      return row
    })
  
  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n')
  
  return csvContent
}

// Function to get unique market types
const getUniqueMarketTypes = (data) => {
  if (!data || data.length === 0) return []
  return [...new Set(data.map(item => item.market_type).filter(Boolean))]
}

// Function to download CSV/Excel
const downloadCSV = async () => {
  console.log('downloadCSV called, downloadType:', downloadType.value)
  console.log('hasData:', hasData.value)
  
  if (!hasData.value) {
    console.warn('No milestone data available for download')
    return
  }
  
  isDownloading.value = true
  
  try {
    // Get the milestones data
    const data = milestonesStore.getMilestones
    console.log('Data length:', data?.length)
    
    if (downloadType.value === 'single') {
      console.log('Processing single sheet download')
      // Single sheet download (CSV) - only current market
      const currentMarketType = getCurrentMarketType()
      console.log('Current market type:', currentMarketType)
      const csvContent = convertToCSV(data, currentMarketType)
      const marketSuffix = currentMarketType ? `-${currentMarketType}` : ''
      downloadFile(csvContent, `${props.fileName}-${props.fixtureId}${marketSuffix}-${new Date().toISOString().split('T')[0]}.csv`)
    } else if (downloadType.value === 'json') {
      console.log('Processing JSON download')
      // JSON download - all markets grouped
      const jsonContent = convertToJSON(data)
      downloadFile(jsonContent, `${props.fileName}-${props.fixtureId}-${new Date().toISOString().split('T')[0]}.json`, 'application/json;charset=utf-8;')
    } else {
      console.log('Processing multiple sheets download')
      // Multiple sheets download (Excel) - all markets
      const marketTypes = getUniqueMarketTypes(data)
      console.log('Market types found:', marketTypes)

      if (marketTypes.length === 0) {
        console.warn('No market types found')
        return
      }

      // Create Excel workbook with multiple sheets
      const XLSX = await import('xlsx')
      const workbook = XLSX.utils.book_new()

      marketTypes.forEach(marketType => {
        console.log('Processing market type:', marketType)
        const sheetData = convertToSheetData(data, marketType)
        console.log('Sheet data length for', marketType, ':', sheetData.length)
        if (sheetData.length > 0) {
          const worksheet = XLSX.utils.json_to_sheet(sheetData)
          XLSX.utils.book_append_sheet(workbook, worksheet, marketType)
        } else {
          console.warn('No data for market type:', marketType)
        }
      })

      // Check if we have any sheets
      if (workbook.SheetNames.length === 0) {
        console.error('No sheets were created - no data available')
        return
      }

      // Generate and download Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
      const excelBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      downloadFile(excelBlob, `${props.fileName}-${props.fixtureId}-${new Date().toISOString().split('T')[0]}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    }
  } catch (error) {
    console.error('Error downloading file:', error)
  } finally {
    isDownloading.value = false
    showPopup.value = false
  }
}

// Function to convert data to grid format for Excel
const convertToSheetData = (data, marketType = null) => {
  if (!data || data.length === 0) return []
  
  // Filter data by market type if specified and exclude suspended items
  const filteredData = (marketType ? data.filter(item => item.market_type === marketType) : data)
    .filter(item => {
      // Only show items where is_suspended is false
      return item.is_suspended == false
    })
  
  // Helper function to get over odds value
  const getOverOdds = (item) => {
    return item.milestone_over_odds || item.over_odds || item.odds1 || item.odds || ''
  }
  
  // Helper function to get under odds value
  const getUnderOdds = (item) => {
    return item.milestone_under_odds || item.under_odds || item.odds2 || ''
  }
  
  // Helper function to check if suspended
  const isSuspended = (item) => {
    return (item.milestone_suspended || item.suspended) ? 'Yes' : 'No'
  }
  
  // Group data by player
  const playerGroups = {}
  filteredData.forEach(item => {
    const playerId = item.player_id
    if (!playerGroups[playerId]) {
      playerGroups[playerId] = {
        player_name: item.player?.player_name || 'Unknown Player',
        team_name: item.player_team_name || item.home_team_name || item.away_team_name || 'Unknown Team',
        milestones: {}
      }
    }
    // Store milestone by its line value
    const milestoneKey = item.milestone_line ? item.milestone_line.toFixed(1) : '0.0'
    playerGroups[playerId].milestones[milestoneKey] = {
      over_odds: getOverOdds(item),
      under_odds: getUnderOdds(item),
      suspended: isSuspended(item)
    }
  })
  
  // Get all unique milestone lines and sort them
  const allMilestoneLines = new Set()
  filteredData.forEach(item => {
    if (item.milestone_line) {
      allMilestoneLines.add(item.milestone_line.toFixed(1))
    }
  })
  const sortedMilestoneLines = Array.from(allMilestoneLines).sort((a, b) => parseFloat(a) - parseFloat(b))
  
  // Create rows in grid format - sorted by team, then by player name
  return Object.values(playerGroups)
    .sort((a, b) => {
      // First sort by team name
      const teamComparison = (a.team_name || '').localeCompare(b.team_name || '')
      if (teamComparison !== 0) return teamComparison
      // Then sort by player name
      return (a.player_name || '').localeCompare(b.player_name || '')
    })
    .map(player => {
      const row = {
        'Player Name': player.player_name,
        'Team': player.team_name
      }
      
      // Add milestone values for each line
      sortedMilestoneLines.forEach(line => {
        const milestone = player.milestones[line]
        if (milestone) {
          // Always show over odds value, regardless of suspension status
          const value = milestone.over_odds || '0.00'
          row[`TO REACH: ${line}`] = value
        } else {
          row[`TO REACH: ${line}`] = '0.00'
        }
      })
      
      return row
    })
}

// Function to convert milestone data to JSON format (only selected markets)
const convertToJSON = (data) => {
  if (!data || data.length === 0) return '[]'

  const marketTypes = selectedJsonMarkets.value.length > 0
    ? selectedJsonMarkets.value
    : getUniqueMarketTypes(data)

  const jsonData = {}

  marketTypes.forEach(marketType => {
    const sheetData = convertToSheetData(data, marketType)
    if (sheetData.length > 0) {
      jsonData[marketType] = sheetData
    }
  })

  return JSON.stringify(jsonData, null, 2)
}

// Helper function to download file
const downloadFile = (content, fileName, mimeType = 'text/csv;charset=utf-8;') => {
  console.log('downloadFile called with:', fileName, mimeType)
  const blob = new Blob([content], { type: mimeType })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    console.log('Download initiated for:', fileName)
  } else {
    console.error('Download not supported in this browser')
  }
}

// Function to handle mouse enter for tooltip
const handleMouseEnter = () => {
  showTooltip.value = true
}

// Function to handle mouse leave for tooltip
const handleMouseLeave = () => {
  showTooltip.value = false
}

// Function to handle button click
const handleButtonClick = () => {
  if (hasData.value && !isDownloading.value) {
    showPopup.value = !showPopup.value
  }
}

// Function to handle download type selection
const selectDownloadType = (type) => {
  downloadType.value = type
  downloadCSV()
}

// Toggle a market in JSON selection
const toggleJsonMarket = (market) => {
  const idx = selectedJsonMarkets.value.indexOf(market)
  if (idx === -1) {
    selectedJsonMarkets.value.push(market)
  } else {
    selectedJsonMarkets.value.splice(idx, 1)
  }
}

// Select all available markets for JSON
const selectAllJsonMarkets = () => {
  selectedJsonMarkets.value = [...availableMarketTypes.value]
}

// Deselect all markets for JSON
const deselectAllJsonMarkets = () => {
  selectedJsonMarkets.value = []
}

// Download JSON with selected markets
const downloadJsonWithMarkets = () => {
  if (selectedJsonMarkets.value.length === 0) return
  downloadType.value = 'json'
  downloadCSV()
}

// Function to download CSV/Excel with type parameter
const downloadCSVWithType = (type) => {
  console.log('downloadCSVWithType called with type:', type)
  if (type === 'json') {
    showJsonMarkets.value = !showJsonMarkets.value
    return
  }
  showJsonMarkets.value = false
  downloadType.value = type
  console.log('downloadType set to:', downloadType.value)
  downloadCSV()
}

// Function to get current market type from the UI
const getCurrentMarketType = () => {
  return props.selectedMarketType
}

// Function to close popup
const closePopup = () => {
  showPopup.value = false
  showJsonMarkets.value = false
}

// Handle click outside to close popup
const handleClickOutside = (event) => {
  if (showPopup.value && !event.target.closest('.download-button-container')) {
    showPopup.value = false
    showJsonMarkets.value = false
  }
}

// Add and remove event listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="fixed bottom-6 right-6 z-50 download-button-container">
    <!-- Floating Download Button -->
    <button
      @click="handleButtonClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      :disabled="!hasData || isDownloading"
      class="relative group"
    >
      <!-- Main Button -->
      <div class="w-14 h-14 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-white">
        <i 
          v-if="!isDownloading" 
          class="fas fa-download text-lg group-hover:scale-110 transition-transform duration-200"
        ></i>
        <i 
          v-else 
          class="fas fa-spinner fa-spin text-lg"
        ></i>
      </div>
      
      <!-- Pulse Animation Ring -->
      <div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20"></div>
      
      <!-- Tooltip -->
      <div 
        v-if="showTooltip"
        class="absolute bottom-full right-0 mb-3 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg whitespace-nowrap"
      >
        <div class="flex items-center gap-2">
          <i class="fas fa-file-csv text-green-400"></i>
          <span>{{ 
            isDownloading ? 'Downloading...' : 
            !hasData ? 'No data available' : 
            'Download Milestones Data' 
          }}</span>
        </div>
        
        <!-- Tooltip Arrow -->
        <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
      </div>
      
      <!-- No Data Indicator -->
      <div 
        v-if="!hasData"
        class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
      >
        <i class="fas fa-exclamation text-white text-xs"></i>
      </div>
    </button>
    
    <!-- Download Options Popup -->
    <div 
      v-if="showPopup"
      class="absolute bottom-full right-0 mb-1 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[1200px]"
    >
      <!-- Popup Content - Two Column Layout -->
      <div class="flex">
        <!-- Left Side - Visual Preview (Bigger) -->
        <div class="flex-1 p-6 border-r border-gray-200">
          <h4 class="text-base font-medium text-gray-900 mb-4">Grid Format Preview</h4>
          <div class="bg-gray-50 p-4 rounded-lg">
            <div class="text-sm text-gray-600 mb-3">Sample export format:</div>
            <div class="bg-white border rounded overflow-hidden">
              <!-- Headers -->
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">Player</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">Team</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 1</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 2</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 3</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 5</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 8</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 10</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 15</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 20</div>
                <div class="bg-gray-100 p-3 font-medium border-r border-gray-200">TO REACH: 25</div>
                <div class="bg-gray-100 p-3 font-medium">TO REACH: 30</div>
              </div>
              <!-- Sample Data Rows -->
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="p-3 border-r border-gray-200">Anrijs Miska</div>
                <div class="p-3 border-r border-gray-200">BC Kalev</div>
                <div class="p-3 border-r border-gray-200">1.18</div>
                <div class="p-3 border-r border-gray-200">2.50</div>
                <div class="p-3 border-r border-gray-200">3.25</div>
                <div class="p-3 border-r border-gray-200">5.00</div>
                <div class="p-3 border-r border-gray-200">8.50</div>
                <div class="p-3 border-r border-gray-200">10.25</div>
                <div class="p-3 border-r border-gray-200">15.75</div>
                <div class="p-3 border-r border-gray-200">20.50</div>
                <div class="p-3 border-r border-gray-200">25.25</div>
                <div class="p-3">30.00</div>
              </div>
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="p-3 border-r border-gray-200 bg-gray-50">Gregor Kuuba</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">BC Kalev</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">1.25</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">2.75</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">3.50</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">5.25</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">8.75</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">10.50</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">15.85</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">20.60</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">25.35</div>
                <div class="p-3 bg-gray-50">30.10</div>
              </div>
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="p-3 border-r border-gray-200">Kasper Suui</div>
                <div class="p-3 border-r border-gray-200">BC Kalev</div>
                <div class="p-3 border-r border-gray-200">1.20</div>
                <div class="p-3 border-r border-gray-200">2.60</div>
                <div class="p-3 border-r border-gray-200">3.30</div>
                <div class="p-3 border-r border-gray-200">5.10</div>
                <div class="p-3 border-r border-gray-200">8.60</div>
                <div class="p-3 border-r border-gray-200">10.35</div>
                <div class="p-3 border-r border-gray-200">15.80</div>
                <div class="p-3 border-r border-gray-200">20.55</div>
                <div class="p-3 border-r border-gray-200">25.30</div>
                <div class="p-3">30.05</div>
              </div>
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="p-3 border-r border-gray-200 bg-gray-50">Karolis Gi</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">Tartu Ülikool</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">1.10</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">2.40</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">3.15</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">4.95</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">8.45</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">10.20</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">15.70</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">20.45</div>
                <div class="p-3 border-r border-gray-200 bg-gray-50">25.20</div>
                <div class="p-3 bg-gray-50">29.95</div>
              </div>
              <div class="grid grid-cols-12 gap-0 text-sm font-mono">
                <div class="p-3 border-r border-gray-200">Jalen Henry</div>
                <div class="p-3 border-r border-gray-200">Tartu Ülikool</div>
                <div class="p-3 border-r border-gray-200">1.15</div>
                <div class="p-3 border-r border-gray-200">2.45</div>
                <div class="p-3 border-r border-gray-200">3.20</div>
                <div class="p-3 border-r border-gray-200">5.05</div>
                <div class="p-3 border-r border-gray-200">8.55</div>
                <div class="p-3 border-r border-gray-200">10.30</div>
                <div class="p-3 border-r border-gray-200">15.75</div>
                <div class="p-3 border-r border-gray-200">20.50</div>
                <div class="p-3 border-r border-gray-200">25.25</div>
                <div class="p-3">30.00</div>
              </div>
            </div>
            <div class="text-sm text-gray-500 mt-3">
              ✓ Players grouped by team<br>
              ✓ Sorted alphabetically<br>
              ✓ Shows over odds values
            </div>
            
            <!-- Multiple Sheets Preview -->
            <div class="mt-4 pt-4 border-t border-gray-200">
              <div class="text-sm text-gray-600 mb-2">Multiple Sheets (Excel) - Separate sheet for each market:</div>
              <div class="flex gap-2 flex-wrap">
                <div class="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium border border-blue-200">
                  assists
                </div>
                <div class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium border border-green-200">
                  points
                </div>
                <div class="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium border border-purple-200">
                  rebounds
                </div>
                <div class="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-medium border border-orange-200">
                  blocks
                </div>
                <div class="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium border border-red-200">
                  steals
                </div>
                <div class="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-medium border border-indigo-200">
                  turnovers
                </div>
              </div>
              <div class="text-xs text-gray-500 mt-2">
                Each market gets its own sheet in the Excel file
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right Side - Download Options -->
        <div class="w-64 p-4">
          <h4 class="text-sm font-medium text-gray-900 mb-4">Choose Format</h4>
          <div class="space-y-3">
            <!-- Single Sheet Option -->
            <button
              @click="downloadCSVWithType('single')"
              @mouseenter="hoveredOption = 'single'"
              @mouseleave="hoveredOption = null"
              :disabled="isDownloading"
              class="w-full p-4 text-left hover:bg-blue-50 transition-colors rounded-lg border-2 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="hoveredOption === 'single' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'"
            >
              <div class="flex items-center gap-3">
                <div class="w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center">
                  <div v-if="hoveredOption === 'single'" class="w-3 h-3 bg-blue-500 rounded-full"></div>
                </div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Single Sheet</div>
                  <div class="text-xs text-gray-500">All markets in one CSV file</div>
                </div>
              </div>
            </button>
            
            <!-- Multiple Sheets Option -->
            <button
              @click="downloadCSVWithType('multiple')"
              @mouseenter="hoveredOption = 'multiple'"
              @mouseleave="hoveredOption = null"
              :disabled="isDownloading"
              class="w-full p-4 text-left hover:bg-green-50 transition-colors rounded-lg border-2 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="hoveredOption === 'multiple' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'"
            >
              <div class="flex items-center gap-3">
                <div class="w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center">
                  <div v-if="hoveredOption === 'multiple'" class="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div>
                  <div class="text-sm font-medium text-gray-900">Multiple Sheets</div>
                  <div class="text-xs text-gray-500">Excel file with separate sheet for each market</div>
                </div>
              </div>
            </button>

            <!-- JSON Option -->
            <button
              @click="downloadCSVWithType('json')"
              @mouseenter="hoveredOption = 'json'"
              @mouseleave="hoveredOption = null"
              :disabled="isDownloading"
              class="w-full p-4 text-left hover:bg-orange-50 transition-colors rounded-lg border-2 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="showJsonMarkets ? 'border-orange-500 bg-orange-50' : (hoveredOption === 'json' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300')"
            >
              <div class="flex items-center gap-3">
                <div class="w-5 h-5 rounded border-2 border-gray-300 flex items-center justify-center">
                  <div v-if="hoveredOption === 'json' || showJsonMarkets" class="w-3 h-3 bg-orange-500 rounded-full"></div>
                </div>
                <div class="flex-1">
                  <div class="text-sm font-medium text-gray-900">JSON Export</div>
                  <div class="text-xs text-gray-500">Select markets to include</div>
                </div>
                <i class="fas fa-chevron-down text-gray-400 text-xs transition-transform duration-200" :class="showJsonMarkets ? 'rotate-180' : ''"></i>
              </div>
            </button>

            <!-- JSON Market Picker (expandable) -->
            <div v-if="showJsonMarkets" class="border-2 border-orange-200 rounded-lg p-3 bg-orange-50/50">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-medium text-gray-700">Select Markets</span>
                <div class="flex gap-2">
                  <button @click="selectAllJsonMarkets" class="text-xs text-orange-600 hover:text-orange-800 hover:underline">All</button>
                  <span class="text-xs text-gray-300">|</span>
                  <button @click="deselectAllJsonMarkets" class="text-xs text-orange-600 hover:text-orange-800 hover:underline">None</button>
                </div>
              </div>
              <div class="space-y-1 max-h-40 overflow-y-auto">
                <label
                  v-for="market in availableMarketTypes"
                  :key="market"
                  class="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-orange-100 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    :checked="selectedJsonMarkets.includes(market)"
                    @change="toggleJsonMarket(market)"
                    class="w-3.5 h-3.5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span class="text-gray-800">{{ market }}</span>
                </label>
                <div v-if="availableMarketTypes.length === 0" class="text-xs text-gray-500 px-2 py-1">
                  No markets available in data
                </div>
              </div>
              <button
                @click="downloadJsonWithMarkets"
                :disabled="isDownloading || selectedJsonMarkets.length === 0"
                class="w-full mt-3 px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <i v-if="isDownloading" class="fas fa-spinner fa-spin mr-1"></i>
                <i v-else class="fas fa-download mr-1"></i>
                Download JSON ({{ selectedJsonMarkets.length }} market{{ selectedJsonMarkets.length !== 1 ? 's' : '' }})
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Popup Arrow -->
      <div class="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
    </div>
  </div>
</template>

<style scoped>
/* Custom animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.1;
  }
}

.animate-ping {
  animation: pulse 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* Hover effects */
.group:hover .animate-ping {
  animation-duration: 1s;
}

/* Disabled state */
button:disabled {
  cursor: not-allowed;
}

button:disabled .animate-ping {
  animation: none;
}

/* Tooltip animations */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}
</style>
